import { z } from "zod";

const PAGE_SIZE = 10;

// Only columns the backend can sort by: its `sortBy` has no national ID.
export const driverSortBySchema = z.enum([
  "nameEn",
  "nameAr",
  "licenseNumber",
  "licenseExpiryDate",
  "status",
]);

export const driverStatusSchema = z.enum([
  "PENDING_ACTIVATION",
  "ACTIVE",
  "INACTIVE",
]);

// `from/toExpiryDate` are set together (one date-range picker), as the
// calendar days picked (`YYYY-MM-DD`): the backend takes plain dates, both
// ends inclusive.
export const driversSearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    sortBy: driverSortBySchema.optional(),
    // No .trim() -- the input's value is this field round-tripped through the
    // URL on every keystroke, so trimming here would eat spaces (including a
    // trailing one) as the user types a multi-word search.
    search: z.string().min(1).optional(),
    status: driverStatusSchema.optional(),
    fromExpiryDate: z.string().date().optional(),
    toExpiryDate: z.string().date().optional(),
  })
  .refine(
    (search) =>
      (search.fromExpiryDate === undefined) ===
      (search.toExpiryDate === undefined),
  );

export type DriversSearch = z.infer<typeof driversSearchSchema>;
export type DriverSortBySearch = z.infer<typeof driverSortBySchema>;

// `/drivers/archived` sorts by archive date only (`sort`), so there is no
// `sortBy`. `from/toArchivedAt` are set together (one date-range picker).
export const archivedDriversSearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    // No .trim() -- see driversSearchSchema.
    search: z.string().min(1).optional(),
    fromArchivedAt: z.string().datetime().optional(),
    toArchivedAt: z.string().datetime().optional(),
  })
  .refine(
    (search) =>
      (search.fromArchivedAt === undefined) ===
      (search.toArchivedAt === undefined),
  );

export type ArchivedDriversSearch = z.infer<typeof archivedDriversSearchSchema>;

export const operationalUserSortBySchema = z.enum([
  "nameEn",
  "nameAr",
  "username",
  "status",
]);

export const operationalUserStatusSchema = z.enum([
  "PENDING_ACTIVATION",
  "ACTIVE",
  "INACTIVE",
]);

export const operationalUsersSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
  sort: z.enum(["asc", "desc"]).optional(),
  sortBy: operationalUserSortBySchema.optional(),
  // No .trim() -- see driversSearchSchema.
  search: z.string().min(1).optional(),
  status: operationalUserStatusSchema.optional(),
});

export type OperationalUsersSearch = z.infer<
  typeof operationalUsersSearchSchema
>;
export type OperationalUserSortBySearch = z.infer<
  typeof operationalUserSortBySchema
>;

// `/users/archived` sorts by archive date only (`sort`), so there is no
// `sortBy`. `from/toArchivedAt` are set together (one date-range picker).
export const archivedOperationalUsersSearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    search: z.string().min(1).optional(),
    fromArchivedAt: z.string().datetime().optional(),
    toArchivedAt: z.string().datetime().optional(),
  })
  .refine(
    (search) =>
      (search.fromArchivedAt === undefined) ===
      (search.toArchivedAt === undefined),
  );

export type ArchivedOperationalUsersSearch = z.infer<
  typeof archivedOperationalUsersSearchSchema
>;
